import { ErrorResponse, StageDischargeDto } from "cd-eft/_codegen_/openapi-generator/eft-set";
import MessageCenter from "cd-eft/components/common/MessageCenter/MessageCenterVM";
import { HydraulicFormulaController } from "cd-eft/services/eftSetApi";
import { ByteArrayToImageUrl } from "cd-eft/utils/ImageDownload";
import { formatRound, formatRoundS } from "cd-eft/utils/mathUtilities";
import { action, observable } from "mobx";

interface IExportedValues {
    label: string;
    value?: any;
    section?: boolean;
}

interface StageDischargePlotData {

}
interface StageDischargeData {
    stageDischargeData: number[][];
}

/**
 * Parent class for formula logic. Has variables for content known to come from backend and a loading flag.
 * Use this if all formulas use same API call for initial content setup.
 */
export class FormulaFormVM {
    props;
    @observable isLoading: boolean = false;
    constructor(props) {
        this.props = props;
    }

    // Button Enablers
    @observable disableComputeBtn: boolean = false;
    @observable disablePrintBtn: boolean = false;

    // Content variables - retrieved from getFormData's API call when available.
    @observable formulaTitle: string = '';
    @observable image: string = '';
    @observable formDescription: string = '';// Use in description Block
    @observable hydraulicFormulaImageDescription: string = '';// Use in description Block
    @observable helpLink: string = '';// Use in help button link as source/href.
    @observable helpKeLink: string = '';// Use in help button link as source/href.
    @observable defaultValues: any;// Use these to initialize inputs
    @observable bannerHelpLink: string = '';

    readonly targetDivID = 'formula-graph-div';
    readonly windowOptions = {
        margins: { top: 10, right: 60, bottom: 40, left: 60 },
        height: 425,
        axis: { x: 'Stage (ft)', y: 'Discharge (cfs)' },
        curveType: 'curveLinear',
        ariaLabelForLines: [' peak discharge is ', ' cubic feet per second at ', ' feet'],
        ariaLabel: 'Graph of Discharge versus Elevation showing discharge flow on the y axis and elevation on the x axis.',
    };
    readonly windowOptionsForPrint = {
        margins: { top: 10, right: 60, bottom: 40, left: 60 },
        height: 425,//1700
        axis: { x: 'Stage (ft)', y: 'Discharge (cfs)' },
        curveType: 'curveLinear',
        ariaLabelForLines: [' peak discharge is ', ' cubic feet per second at ', ' feet'],
        ariaLabel: 'Graph of Discharge versus Elevation showing discharge flow on the y axis and elevation on the x axis.',
    };
    // Hardcoded data for now, cahnge to observable and populate in calculation when API ready
    /**
     * stageDischargeData: [
            [Stage as number, Discharge as number],
            [Stage as number, Discharge as number],
        ]
     */
    @observable plotData: StageDischargeData | undefined = undefined;

    // Calculation variables
    @observable calculationResults: any;
    @observable hasResults: boolean = false;

    // Print variables
    @observable exportedInputs: IExportedValues[] = [];
    @observable exportedResults: IExportedValues[] = [];
    @observable saveButtonPopup: boolean = false;
    @action.bound printFormula() {
        // return true if you want proceed to the next page
        this.saveButtonPopup = true;
        return;
    }

    /**
     * Override this function in specific formula VM for expected variables.
     * Look to HoodedInletVM for an example.
     */
    @action.bound initializeDefaultValues() {
        console.warn('Need to override initializeDefaultValues');
    }

    protected hasErrorContent(errorResponse: ErrorResponse | undefined) {
        if (errorResponse && errorResponse.errorCode && errorResponse.errorMessage && errorResponse.errorType
            && errorResponse.errorCode !== '' && errorResponse.errorMessage !== '' && errorResponse.errorType !== '') {
                return true;
        }
        else {
            return false;
        }
    }

    /**
     * Call on render to retrieve form content from backend.
     * @param formulaDefaultId The hydraulic_formula_default_id for which we are loading content.
     */
    @action.bound async getFormData(formulaStringId: string) {
        try {
            const list = formulaStringId.split('_');
            const idCheck = list[list.length - 1];
            if (isNaN(parseInt(idCheck)) || parseInt(idCheck) <= 0) {
                return;
            }
            this.isLoading = true;
            // API call code here
            // Not implemented yet, but should get above variables.
            const response = await HydraulicFormulaController.getHydraulicDefaultFromulaUsingGET(formulaStringId);
            if (response && response.data) {
                const data = response.data;
                if (this.hasErrorContent(data.errorResponse)) {
                    MessageCenter.addNotification(data.errorResponse!.errorMessage!, data.errorResponse!.errorType!, data.errorResponse!.errorCode,
                    data.errorResponse?.urlPath ? [{
                        text: 'Help',
                        callback: () => {
                            window.open(data.errorResponse?.urlPath, '_blank');
                        }
                    }] : undefined)
                }
                else if (data.hydraulicFormulaName) {
                    this.formulaTitle = data.hydraulicFormulaName;
                    this.defaultValues = JSON.parse(data.defaultValueJson!);
                    this.formDescription = data.hydraulicFormulaDescription!;
                    this.hydraulicFormulaImageDescription = data.hydraulicFormulaImageDescription!;
                    // this.image = data.hydraulicFormulaImage!;
                    if (data.hydraulicFormulaImage) {
                        this.image = ByteArrayToImageUrl(data.hydraulicFormulaImage as any);
                    }
                    this.bannerHelpLink = data.helpUrl!; // Not currently in response
                    this.initializeDefaultValues();
                    document.title = `NRCS-NETS ${data.hydraulicFormulaName}`
                }
            }
        }
        catch (error) {
            console.error(error);
            // If error would contain error message, call MC
        }
        finally {
            this.isLoading = false;
        }
    }

    @action.bound removeAndSetPlotData(obj) {
        this.setPlotData(obj.stageDischargeList);
        delete obj.stageDischargeList;
        return obj;
    }

    @action.bound setPlotData (data: StageDischargeDto[]) {
        if (data === undefined || data.length === 0) {
            this.plotData = undefined;
            return;
        }
        const newArray = data.map((pair) => [pair.stage, pair.discharge]);
        this.plotData = {
            stageDischargeData: newArray as any
        }
    }
    @action.bound resetPlotData() {
        this.plotData = undefined;
    }
    roundingHelperString(value: number, maxDecimals: number) {
        return formatRoundS(value, maxDecimals);
    }
    roundingHelperNumber(value: number, maxDecimals: number) {
        return formatRound(value, maxDecimals);
    }
}

export abstract class FormulaFormInterface {
    readonly formLabelsForExport: any;
    readonly resultLabelsForExport: any;
}

/**
 * Abstract VM version in case the API call needs to behave differently per formula.
 * If a single API call gets all data the same way for every formula, use normal class. If there are multiple API endpoints
 * for different formula, use and expand this abstract class instead. Variables should behave mostly the same, but
 * we will define function code in the implementing formula specific class.
 */
// export abstract class FormulaFormVMTemplate {
//     props;
//     constructor (props) {
//         this.props = props;
//     }

//     @observable images: any[] = [];
//     @observable formDescription: string = '';
//     @observable helpLink: string = '';

//     @action.bound async getFormData() {}
// }